/**
 * RelayCard — single-relay tile in the site dashboard.
 *
 * Flat M3 design: clean Card shell, squircle power quick-action button
 * tinted by live power state, relay title + muted load-type chip, load
 * list, schedule list (with day-of-week squircle pills), and an ambient
 * 24h power-area sparkline footer.
 *
 * Slots: `headerExtras` adds nodes to the header's right-action cluster;
 * `children` replaces the schedule section with caller-supplied content
 * (e.g. the charting page's contract panel) while keeping the shell,
 * header, and load carousel identical to the dashboard.
 */
import { WbSunnyRounded } from '@mui/icons-material';
import PowerSettingsNewIcon from '@mui/icons-material/PowerSettingsNew';
import SettingsOutlinedIcon from '@mui/icons-material/SettingsOutlined';
import { alpha, Box, Card, Chip, IconButton, Skeleton, Stack, Tooltip, Typography } from '@mui/material';
import { blue, green, grey, red } from '@mui/material/colors';
import { parseISO } from 'date-fns';
import { toZonedTime } from 'date-fns-tz';
import React, { useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';

import { useBluetooth } from '@sophocles/components/bluetooth/BluetoothContext';
import WindowSizeHOC from '@sophocles/components/WindowSizeHOC';
import { getAuthUserSelector } from '@sophocles/features/auth/authSelectors';
import { selectCapabilities } from '@sophocles/features/capabilities';
import {
  beginCommandDispatch,
  endCommandDispatch,
  selectSiteCommandLocked,
  selectSiteFeatureAssociationByKey,
} from '@sophocles/features/entities/site';
import { getLoadTypeCapabilities, selectRelayLoadTypeCapsById } from '@sophocles/features/reference';
import { scheduleViewSelector } from '@sophocles/features/sites/slices/selectedStructure/selectors';
import { addToast } from '@sophocles/features/toast';
import { siteCalendarPath } from '@sophocles/router/paths';
import { windowSizes } from '@sophocles/utils';
import { SMARTLINK_MODEL } from '@sophocles/utils/const';
import { userPermCheck } from '@sophocles/utils/userPermissionUtils';
import RelayCommandsPopup from '@sophocles/views/sites/components/RelayBoxes/RelayCommandsPopup';
import RelayLoadCarousel from '@sophocles/views/sites/components/RelayCards/RelayLoadCarousel';
import RelayScheduleNextChangeLabel from '@sophocles/views/sites/components/RelayCards/RelayScheduleNextChangeLabel';
import ScheduleList from '@sophocles/views/sites/components/RelayCards/ScheduleList';
import ScheduleTimeline from '@sophocles/views/sites/components/RelayCards/ScheduleTimeline';

// ── Power-state model ──────────────────────────────────────────────────────
// Mirrors RelayCommandsPopup's effectivePowerState, plus an unconfigured case.

/**
 * Returns 'unconfigured' | 'unknown' | 'on' | 'off' for a plain-JS relay object.
 */
function derivePowerState(relay) {
  const relayLoads = relay.loads;
  if (!relayLoads || relayLoads.length === 0) return 'unconfigured';

  const primary = relay.power_state;
  const secondaryRelay = relay.secondary_relay ?? null;
  const secondary = secondaryRelay ? (secondaryRelay.power_state ?? null) : null;

  if (primary === null && secondary === null) return 'unknown';

  const isOn = primary === true && (secondary === null || secondary === true);
  return isOn ? 'on' : 'off';
}

const STATE_COLOR = {
  on: green[600],
  off: red[600],
  unknown: blue[600],
  unconfigured: grey[500],
};

// ── RelayCard ──────────────────────────────────────────────────────────────

function RelayCard({
  loading,
  relay,
  relays,
  siteId,
  unit,
  siteTimeZone,
  customer,
  isBillboard,
  hasCharting,
  usesCalendar,
  params,
  windowWidth,
  curfew,
  onClickEditSchedule,
  siteIccid,
  nextSunriseRaw = null,
  nextSunsetRaw = null,
  hideCommands = false,
  hideOptions = false,
  customCommands = null,
  headerExtras = null,
  children = null,
}) {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [isCommandPopoverOpen, setIsCommandPopoverOpen] = useState(false);
  const { isConnectedToSite, sendRelayCommand } = useBluetooth();
  const bleCommandInFlightRef = useRef(false);
  const capabilities = useSelector(selectCapabilities);
  const commandLocked = useSelector(selectSiteCommandLocked(siteId ?? 0));
  const loadTypeCapsById = useSelector(selectRelayLoadTypeCapsById);
  // Legacy Immutable user Map — still needed by ScheduleList's userPermCheck
  // and scheduleStart/scheduleEnd time formatting until those are migrated.
  const user = useSelector(getAuthUserSelector);

  const onMobile = windowWidth <= windowSizes.tablet.width;

  const fw = unit?.firmware_version ?? null;
  const maxSchedules = fw?.schedules_per_relay ?? 2;
  const modelId = unit?.model?.id ?? 0;
  const hasFirmware = fw != null;
  const isCommercialUnit = hasFirmware ? (unit?.uses_zoneinfo_status ?? false) : !isBillboard;
  const isUnitDc = [
    SMARTLINK_MODEL.SL_DC_WITHOUT_DOW,
    SMARTLINK_MODEL.SL_DC_WITH_DOW,
    SMARTLINK_MODEL.SL_TWO_PORT_DC,
  ].includes(modelId);

  const scheduleView = useSelector(scheduleViewSelector);

  const dimmerFeature = useSelector(selectSiteFeatureAssociationByKey(siteId ?? 0, 'DIMMING'));
  const dimmerFeatureEnabled = dimmerFeature?.is_enabled ?? false;
  const dimmerAvailable = isUnitDc || dimmerFeatureEnabled;

  // relay_controller is null when there is no unit; used for the sparkline unit id.
  const relayController = unit ?? null;

  const relayNum = relay.relay_number;
  const relayLoads = relay.loads ?? [];
  const hasRelayLoads = relayLoads.length > 0;
  const schedules = relay.schedules ?? [];
  // A normally-closed relay is always powered, so the backend forces its
  // scheduling off; surface that to the schedule list so it explains the state
  // rather than rendering empty "N. Disabled" rows.
  const schedulingEnabled = relay.scheduling_enabled ?? true;
  const normallyClosed = relay.normally_closed ?? false;

  // Resolved once here from the site snapshot's raw ISO sun times, in the
  // site's own time zone, for both the timeline strip and the calendar-mode
  // "next change" label — same conversion RelayBlade/EditSchedulesDialog use.
  const nextSunrise = nextSunriseRaw != null ? toZonedTime(parseISO(nextSunriseRaw), siteTimeZone ?? 'UTC') : null;
  const nextSunset = nextSunsetRaw != null ? toZonedTime(parseISO(nextSunsetRaw), siteTimeZone ?? 'UTC') : null;

  const secondaryRelay = relay.secondary_relay ?? null;

  const canSendRelayCommands = capabilities.can_send_command ?? false;

  const relayId = relay.id;

  // ── Power state ────────────────────────────────────────────────────────
  const powerState = derivePowerState(relay);
  const stateColor = STATE_COLOR[powerState];

  // ── Title ─────────────────────────────────────────────────────────────
  const title =
    `${t('sites.relay')} ` + relayNum + (secondaryRelay ? ` + ${t('sites.relay')} ` + secondaryRelay.relay_number : '');
  // Compact identity for the condensed card title; the full title is the tooltip.
  const relayShortLabel = `R${relayNum}${secondaryRelay ? `+${secondaryRelay.relay_number}` : ''}`;

  const loadType = relay.load_type?.name ?? null;
  // Mirror RelayLoadForm's fixture gate: fixtures only apply to loads whose
  // load type supports fixtures. Hide the fixture meta when the edit form
  // wouldn't expose the field either.
  const showFixtures = getLoadTypeCapabilities(loadTypeCapsById, relay.load_type?.id ?? 0).supports_light_fixtures;

  // ── Weather icon ──────────────────────────────────────────────────────
  // Effective weather control (site config vs. site-group fallback, ANDed
  // with the WEATHER feature) is resolved server-side into the contract row.
  const showWeatherIcon = relay.weather_controlled === true;

  // ── Loading state ──────────────────────────────────────────────────────
  if (loading) {
    return (
      <Card
        sx={{
          overflow: 'hidden',
          height: '100%',
          width: '100%',
          display: 'flex',
          flexDirection: 'column',
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, px: 1.5, py: 1.5 }}>
          <Skeleton variant="rounded" width={32} height={32} sx={{ borderRadius: '9px', flexShrink: 0 }} />
          <Stack direction="row" spacing={1} alignItems="center" sx={{ flex: 1, minWidth: 0 }}>
            <Skeleton variant="text" width={32} sx={{ fontSize: '1rem' }} />
            <Skeleton variant="rounded" width={96} height={20} sx={{ borderRadius: 'var(--button-radius)' }} />
          </Stack>
          <Stack direction="row" spacing={0.5} sx={{ flexShrink: 0 }}>
            <Skeleton variant="circular" width={24} height={24} />
            <Skeleton variant="circular" width={24} height={24} />
          </Stack>
        </Box>

        <Box sx={{ px: 1.5, pb: 1.5 }}>
          <Box
            sx={{
              border: '1px solid',
              borderColor: 'divider',
              borderRadius: 1,
              p: 1,
              bgcolor: 'action.hover',
            }}
          >
            <Stack direction="row" spacing={1} alignItems="center">
              <Skeleton variant="rounded" width={54} height={54} sx={{ borderRadius: 1 }} />
              <Stack spacing={0.5} sx={{ flex: 1, minWidth: 0 }}>
                <Skeleton variant="text" width="68%" />
                <Skeleton variant="text" width="44%" />
              </Stack>
            </Stack>
          </Box>
        </Box>

        <Box sx={{ borderTop: 1, borderColor: 'divider', px: 1.5, py: 1.25 }}>
          <Stack spacing={1}>
            {[0, 1].map(index => (
              <Stack key={`relay-card-loading-schedule-${index}`} direction="row" spacing={1} alignItems="center">
                <Stack direction="row" spacing={0.5}>
                  {[0, 1, 2].map(day => (
                    <Skeleton
                      key={`relay-card-loading-schedule-${index}-${day}`}
                      variant="rounded"
                      width={22}
                      height={22}
                      sx={{ borderRadius: 'var(--radius-sm)' }}
                    />
                  ))}
                </Stack>
                <Skeleton variant="text" width="36%" sx={{ ml: 'auto' }} />
              </Stack>
            ))}
          </Stack>
        </Box>

        <Box
          sx={{
            mt: 'auto',
            px: 1.5,
            pb: 1.25,
            display: 'grid',
            gridTemplateColumns: 'repeat(8, 1fr)',
            gap: 0.5,
            alignItems: 'end',
          }}
        >
          {[18, 28, 22, 42, 34, 48, 26, 38].map((height, index) => (
            <Skeleton
              key={`relay-card-loading-spark-${index}`}
              variant="rounded"
              height={height}
              sx={{ borderRadius: 'var(--radius-sm)' }}
            />
          ))}
        </Box>
      </Card>
    );
  }

  // ── BLE relay toggle ──────────────────────────────────────────────────
  const isBleConnected = isConnectedToSite(siteIccid);

  const handleBleSendRelay = async () => {
    if (commandLocked || bleCommandInFlightRef.current) return;

    bleCommandInFlightRef.current = true;
    const requestId = dispatch(beginCommandDispatch({ siteId }));
    if (requestId == null) {
      bleCommandInFlightRef.current = false;
      return;
    }
    try {
      const result = await sendRelayCommand(relay.relay_number);
      if (result.success) return;
      dispatch(
        addToast({
          title: t('general.error', 'Error'),
          message: result.error || t('commands.relayCommandFailed', 'Relay command failed'),
          level: 'error',
          dismissible: true,
        }),
      );
    } catch (error) {
      dispatch(
        addToast({
          title: t('general.error', 'Error'),
          message: error?.message || t('commands.relayCommandFailed', 'Relay command failed'),
          level: 'error',
          dismissible: true,
        }),
      );
    } finally {
      bleCommandInFlightRef.current = false;
      dispatch(endCommandDispatch({ siteId, requestId }));
    }
  };

  const blePowerBgColor = powerState === 'on' ? green[500] : powerState === 'off' ? red[700] : null;

  const bleCommandButton = canSendRelayCommands ? (
    <Tooltip
      key="ble_relay_command"
      title={
        commandLocked
          ? t('commandActivity.commandsLocked', 'Wait for the queued command to finish before sending another.')
          : t('commands.relayToggleBle', 'Toggle relay via Bluetooth')
      }
    >
      <span>
        <IconButton size="small" disabled={commandLocked} onClick={handleBleSendRelay} sx={{ p: 0 }}>
          <Box
            sx={{
              borderRadius: '50%',
              backgroundColor: blePowerBgColor,
              width: 26,
              height: 26,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              border: blePowerBgColor ? theme => `1px solid ${theme.palette.common.white}` : undefined,
            }}
          >
            <PowerSettingsNewIcon fontSize="small" />
          </Box>
        </IconButton>
      </span>
    </Tooltip>
  ) : null;

  // ── Power quick-action trigger (the circular power button) ────────────
  // We render it as the `display` prop of RelayCommandsPopup so that
  // SimpleTwoButtonPopup's internal <button> anchors the popover correctly.
  // When the user lacks the permission, we render a plain non-interactive icon.
  const powerButtonDisplay = (
    <Box
      sx={{
        width: 32,
        height: 32,
        borderRadius: 'var(--card-icon-radius)',
        bgcolor: alpha(stateColor, 0.14),
        color: stateColor,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        '&:hover': canSendRelayCommands && !commandLocked ? { bgcolor: alpha(stateColor, 0.24) } : undefined,
        transition: 'background-color var(--duration-normal) var(--easing-standard)',
        flexShrink: 0,
      }}
    >
      <PowerSettingsNewIcon fontSize="small" />
    </Box>
  );

  // A relay with no loads is still addressable — commands target the relay
  // number, so an unwired relay can be switched to prove it works.
  const powerActionButton = !hideCommands ? (
    customCommands != null ? (
      customCommands
    ) : isBleConnected ? (
      bleCommandButton
    ) : canSendRelayCommands ? (
      // The command popover is modal: its backdrop swallows the pointer leaving
      // the button, so the hover tooltip would otherwise hang over the popover's
      // own Confirm button. An empty title keeps the tooltip shut while the
      // popover is up.
      <Tooltip title={isCommandPopoverOpen ? '' : t('sites.relayPowerCommands', 'Power on/off')}>
        <span>
          <RelayCommandsPopup
            key="relay_commands_popup"
            isCommercialUnit={isCommercialUnit}
            siteId={siteId}
            relay={relay}
            onMobile={onMobile}
            stateColor={stateColor}
            onOpenChange={setIsCommandPopoverOpen}
          />
        </span>
      </Tooltip>
    ) : (
      <Tooltip
        title={
          {
            on: t('sites.on', 'On'),
            off: t('sites.off', 'Off'),
            unknown: t('general.unknown', 'Unknown'),
            unconfigured: t('sites.unusedRelay', 'Unused Relay'),
          }[powerState]
        }
      >
        {powerButtonDisplay}
      </Tooltip>
    )
  ) : (
    powerButtonDisplay
  );

  const configureLabel = hasRelayLoads ? t('actions.configure', 'Configure') : t('actions.setup', 'Setup');

  // siteCalendarPath throws on a missing param — only wire the strip's tap
  // navigation where the full site context (not just customer/site-group) is
  // available, so an incomplete params object degrades to a no-op instead of
  // a partial/incorrect URL.
  const canNavigateToCalendarView = params?.cust_id != null && params?.sitegroup_id != null && params?.site_id != null;
  const handleNavigateToCalendarView = canNavigateToCalendarView
    ? () =>
        navigate(
          `${siteCalendarPath({ cust_id: params.cust_id, sitegroup_id: params.sitegroup_id, site_id: params.site_id })}?r=${relayId}`,
        )
    : () => {};

  return (
    <Card
      sx={{
        overflow: 'hidden',
        height: '100%',
        width: '100%',
        display: 'flex',
        flexDirection: 'column',
      }}
    >
      {/* ── Header ─────────────────────────────────────────────────── */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, px: 1.5, py: 1.5 }}>
        {powerActionButton}

        <Stack direction="row" spacing={1} alignItems="center" sx={{ flex: 1, minWidth: 0 }}>
          <Tooltip title={title}>
            <Typography sx={{ fontWeight: 700, flexShrink: 0 }}>{relayShortLabel}</Typography>
          </Tooltip>
          {loadType ? (
            <Chip
              label={loadType}
              size="small"
              sx={{
                height: 20,
                minWidth: 0,
                bgcolor: 'action.hover',
                color: 'text.secondary',
                fontWeight: 500,
              }}
            />
          ) : null}
        </Stack>

        {/* Right actions: weather icon + configure cog (jumps straight to editing) */}
        <Stack direction="row" alignItems="center" spacing={0.5} sx={{ flexShrink: 0 }}>
          {headerExtras}
          {showWeatherIcon ? (
            <Tooltip title={t('sites.controlledByWeather', 'Controlled by Weather')}>
              <WbSunnyRounded fontSize="small" sx={{ color: 'text.secondary' }} />
            </Tooltip>
          ) : null}
          {!hideOptions ? (
            <Tooltip title={configureLabel}>
              <IconButton
                size="small"
                onClick={() => onClickEditSchedule(relayId, null, hasRelayLoads ? 'config' : 'loads')}
                aria-label={configureLabel}
              >
                <SettingsOutlinedIcon fontSize="small" sx={{ color: 'text.secondary' }} />
              </IconButton>
            </Tooltip>
          ) : null}
        </Stack>
      </Box>

      {/* ── Body: loads then schedules ─────────────────────────────── */}
      <RelayLoadCarousel
        siteTimeZone={siteTimeZone}
        hasCharting={hasCharting}
        canSetUp={userPermCheck(user, 'pliny.add_relayload')}
        addPanelFunction={() => {
          onClickEditSchedule(relayId, null, 'loads');
        }}
        onPanelClicked={() => {
          onClickEditSchedule(relayId, null, 'loads');
        }}
        showFixtures={showFixtures}
        loads={relayLoads}
      />

      {children != null ? (
        children
      ) : hasRelayLoads ? (
        usesCalendar ? (
          <>
            {/* Hardware-truth resolved timeline for calendar-managed relays —
                shows what the controller will actually do, not calendar intent.
                Tapping it jumps to the calendar view with this relay preselected. */}
            <ScheduleTimeline
              schedules={schedules}
              user={user}
              dimmerAvailable={dimmerAvailable}
              onClickEditSchedule={handleNavigateToCalendarView}
              nextSunrise={nextSunrise}
              nextSunset={nextSunset}
              dayOfWeekScheduling={fw?.supports_day_of_week_schedules ?? false}
            />
            <RelayScheduleNextChangeLabel
              schedules={schedules}
              user={user}
              nextSunrise={nextSunrise}
              nextSunset={nextSunset}
              siteTimeZone={siteTimeZone}
            />
          </>
        ) : (
          <ScheduleList
            isBillboard={isBillboard}
            usesCalendar={usesCalendar}
            dimmerAvailable={dimmerAvailable}
            params={params}
            user={user}
            schedules={schedules}
            schedulingEnabled={schedulingEnabled}
            normallyClosed={normallyClosed}
            supportsDayOfWeek={fw?.supports_day_of_week_schedules ?? false}
            siteTimeZone={siteTimeZone}
            relay={relay}
            max={maxSchedules}
            modelId={modelId}
            curfew={curfew}
            scheduleView={scheduleView}
            onClickEditSchedule={scheduleId => {
              onClickEditSchedule(relayId, scheduleId, 'schedules');
            }}
          />
        )
      ) : null}

      <Box sx={{ height: 8 }} />
    </Card>
  );
}

const RelayCardContainer = WindowSizeHOC(RelayCard);

export default RelayCardContainer;
